import type { ReactNode } from 'react';
import { Navbar } from '@/components/sections/navbar';
import { SiteFooter } from '@/components/sections/site-footer';
import { ScrollReveal } from '@/components/ui/scroll-reveal';

/**
 * LegalPageWrap — wrapper layout pour les pages SEO indexables (Carnet/Lexique/Colophon).
 * Pattern minimal Océanne (PAS magazine éditorial : 0 numeral, 0 ChapterMarker, 0 Fleuron,
 * 0 drop cap — voir mémoire `feedback_ban_magazine_editorial_default.md`).
 *
 * Structure : Navbar + main centré (eyebrow + h1 + hairline + intro) + body slot + Footer.
 * Cohérent avec les routes existantes `/lexique` et `/carnet` d'Océanne.
 */

interface LegalPageWrapProps {
  eyebrow: string;
  title: string;
  intro?: string;
  /** Largeur max du contenu. Défaut max-w-3xl (lexique-style). max-w-4xl pour Carnet (resources). */
  maxWidth?: 'max-w-3xl' | 'max-w-4xl';
  children: ReactNode;
}

export function LegalPageWrap({
  eyebrow,
  title,
  intro,
  maxWidth = 'max-w-3xl',
  children
}: LegalPageWrapProps) {
  return (
    <div className="flex min-h-[100dvh] flex-col bg-background text-foreground">
      <Navbar />
      <main id="main" className="flex-grow py-16 md:py-24">
        <div className={`mx-auto ${maxWidth} px-5 md:px-8`}>
          {/* Header centré (cohérent avec /lexique et /carnet existantes Océanne) */}
          <div className="mb-12 text-center md:mb-16">
            {/* Phase 27.81b — text-secondary (sage) sur cream = 2.5 contrast (fail).
                Switch to text-foreground/80 (warm slate /80) = ~5.5 contrast (WCAG AA pass).
                Italic Garamond preserve l'editorial feel. */}
            <ScrollReveal
              as="span"
              className="mb-4 block font-display text-base italic tracking-wide text-foreground/80"
            >
              {eyebrow}
            </ScrollReveal>
            <h1 className="mb-6 font-display text-3xl leading-[1.2] text-balance text-foreground md:text-5xl">
              {title}
            </h1>
            <div className="mx-auto mb-6 h-px w-16 bg-primary/60" aria-hidden="true" />
            {intro && (
              <ScrollReveal
                delay={200}
                as="p"
                className="mx-auto max-w-2xl font-sans text-base text-pretty hyphens-auto text-muted-foreground"
              >
                {intro}
              </ScrollReveal>
            )}
          </div>

          {/* Body */}
          {children}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
